import { RevenueDeltas, RevenueMeasures } from "../../apis";

export type CategoryMetric = "netRevenue" | "grossProfit" | "costOfGoods";

export const formatXof = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return "—";
  return `${Math.round(amount).toLocaleString("en-US")} FCFA`;
};

export const formatPercent = (
  value: number | null | undefined,
  fractionDigits = 0
): string => {
  if (value === null || value === undefined) return "—";
  return `${value.toFixed(fractionDigits)}%`;
};

export const formatSignedPercent = (
  value: number | null | undefined,
  fractionDigits = 1
): string => {
  if (value === null || value === undefined) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(fractionDigits)}%`;
};

/**
 * A node's share of the period, or null when the period gives nothing to take a
 * share of.
 *
 * The base has to be positive for the result to mean anything. Dividing by a
 * period that lost money flips every sign: the category responsible for the loss
 * comes out as a large positive contribution and the profitable ones come out
 * negative. A loss-making period has no meaningful split, so say so with null
 * rather than print a number that reads backwards.
 */
export const shareOf = (value: number, total: number): number | null => {
  if (total <= 0) return null;
  return (value / total) * 100;
};

/**
 * Width for a share bar. A bar cannot render a negative or over-full share, so
 * clamp — the exact figure is always spelled out in the label beside it.
 */
export const barPercent = (share: number | null): number => {
  if (share === null) return 0;
  return Math.max(0, Math.min(100, share));
};

// The backend's `deltas` object only carries units/revenue/resellerEarnings/
// netRevenue/costOfGoods — there is no percent field for grossProfit. Derive it
// from `previous.grossProfit` instead of leaving the "Bénéfice" delta badge blank.
const deriveGrossProfitDelta = (
  current: number,
  previous: RevenueMeasures | null
): number | null => {
  if (!previous || previous.grossProfit === 0) return null;
  return (
    ((current - previous.grossProfit) / Math.abs(previous.grossProfit)) * 100
  );
};

export const getRowMetricDelta = (
  node: RevenueMeasures & {
    deltas: RevenueDeltas | null;
    previous: RevenueMeasures | null;
  },
  metric: CategoryMetric
): number | null => {
  if (metric !== "grossProfit") return node.deltas?.[metric] ?? null;
  return deriveGrossProfitDelta(node.grossProfit, node.previous);
};

export const getTotalsMetricDelta = (
  totals: RevenueMeasures,
  previousTotals: RevenueMeasures | null,
  deltas: RevenueDeltas | null,
  metric: CategoryMetric
): number | null => {
  if (metric !== "grossProfit") return deltas?.[metric] ?? null;
  return deriveGrossProfitDelta(totals.grossProfit, previousTotals);
};
